import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
    "Quanto costa un portale di lavaggio?",
    "Che attrezzature avete per officine?",
    "Quanto è veloce la vostra assistenza?",
    "Fate impianti self-service chiavi in mano?",
];

const getSessionId = () => {
    let id = localStorage.getItem("ftm_chat_session");
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem("ftm_chat_session", id);
    }
    return id;
};

const WELCOME = {
    role: "assistant",
    content:
        "Benvenuto in FTM Group. Sono l'assistente virtuale: posso aiutarti su impianti Christ Wash Systems, attrezzature per officine, revisioni e assistenza. Come posso esserti utile?",
};

export const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([WELCOME]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const listRef = useRef(null);
    const sessionId = useRef(getSessionId());

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, open]);

    const send = async (text) => {
        const message = (text ?? input).trim();
        if (!message || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const res = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    const data = JSON.parse(part.slice(6));
                    if (data.delta) {
                        setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = {
                                role: "assistant",
                                content: copy[copy.length - 1].content + data.delta,
                            };
                            return copy;
                        });
                    } else if (data.error) {
                        setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = { role: "assistant", content: data.error };
                            return copy;
                        });
                    }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    content:
                        "Non riesco a rispondere in questo momento. Scrivici a info@ftmgroup.it o usa il modulo contatti.",
                };
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    };

    return (
        <>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="chat-panel"
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.96 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bottom-24 right-4 z-50 flex h-[540px] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_32px_80px_-16px_rgba(11,31,58,0.35)] sm:right-8"
                    >
                        <div className="flex items-center gap-3 bg-[#0B1F3A] px-5 py-4">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E30613]">
                                <Sparkles className="h-5 w-5 text-white" />
                            </div>
                            <div className="flex-1">
                                <div className="font-display text-sm font-bold text-white">
                                    Assistente FTM
                                </div>
                                <div className="flex items-center gap-1.5 text-[11px] text-white/60">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                    Online · risponde in pochi secondi
                                </div>
                            </div>
                            <button
                                data-testid="chat-close-button"
                                onClick={() => setOpen(false)}
                                className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                                aria-label="Chiudi chat"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div
                            ref={listRef}
                            data-testid="chat-message-list"
                            data-lenis-prevent
                            className="flex-1 space-y-4 overflow-y-auto bg-[#F8FAFC] px-4 py-5"
                        >
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`chat-message-${m.role}-${i}`}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                                            m.role === "user"
                                                ? "rounded-br-md bg-[#0B1F3A] text-white"
                                                : "rounded-bl-md border border-slate-200 bg-white text-[#0B1F3A] shadow-sm"
                                        }`}
                                    >
                                        {m.content ||
                                            (streaming && i === messages.length - 1 ? (
                                                <Loader2 className="h-4 w-4 animate-spin text-[#4F5D70]" />
                                            ) : null)}
                                    </div>
                                </div>
                            ))}
                            {messages.length === 1 && (
                                <div className="flex flex-wrap gap-2 pt-2" data-testid="chat-suggestions">
                                    {SUGGESTIONS.map((s, i) => (
                                        <button
                                            key={s}
                                            data-testid={`chat-suggestion-${i}`}
                                            onClick={() => send(s)}
                                            className="rounded-full border border-[#0B1F3A]/15 bg-white px-3.5 py-2 text-xs font-semibold text-[#0B1F3A] transition-[background-color,border-color] duration-300 hover:border-[#E30613] hover:bg-[#E30613]/5"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                send();
                            }}
                            className="flex items-center gap-2 border-t border-slate-100 bg-white p-3"
                        >
                            <input
                                data-testid="chat-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Scrivi la tua domanda…"
                                className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-[#0B1F3A] outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:border-[#0B1F3A] focus:ring-4 focus:ring-[#0B1F3A]/[0.07]"
                            />
                            <button
                                type="submit"
                                data-testid="chat-send-button"
                                disabled={streaming || !input.trim()}
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E30613] text-white transition-[background-color,transform] duration-300 hover:bg-[#C20410] active:scale-90 disabled:opacity-40"
                                aria-label="Invia messaggio"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                data-testid="chat-toggle-button"
                onClick={() => setOpen(!open)}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.92 }}
                className="fixed bottom-6 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#E30613] text-white shadow-[0_16px_40px_-8px_rgba(227,6,19,0.55)] sm:right-8"
                aria-label="Apri assistente virtuale"
            >
                <AnimatePresence mode="wait">
                    {open ? (
                        <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                            <X className="h-6 w-6" />
                        </motion.span>
                    ) : (
                        <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                            <MessageCircle className="h-6 w-6" />
                        </motion.span>
                    )}
                </AnimatePresence>
            </motion.button>
        </>
    );
};
